/**
 * Listing-template builder — channel-aware.
 * `buildListingTemplate(watch, {servicesCount, channel})` returns {title, body}.
 * When the channel is not supported it falls back to the eBay template.
 *
 * Pure function — no DOM, no side effects, easy to unit-test.
 */

// Unicode bold sans-serif — renders as visually bold in any plain-text or
// rich-text editor (eBay's standard editor, Chrono24, Etsy, Instagram, email).
// We use this instead of <strong> tags because most marketplace WYSIWYG editors
// show literal HTML tags rather than rendering them.
function bold(s) {
  if (s == null) return s;
  let out = "";
  for (const ch of String(s)) {
    const cp = ch.codePointAt(0);
    if (cp >= 0x41 && cp <= 0x5A) {        // A–Z → 𝗔–𝗭
      out += String.fromCodePoint(0x1D5D4 + (cp - 0x41));
    } else if (cp >= 0x61 && cp <= 0x7A) { // a–z → 𝗮–𝘇
      out += String.fromCodePoint(0x1D5EE + (cp - 0x61));
    } else if (cp >= 0x30 && cp <= 0x39) { // 0–9 → 𝟬–𝟵
      out += String.fromCodePoint(0x1D7EC + (cp - 0x30));
    } else {
      out += ch;
    }
  }
  return out;
}

function decadeOf(watch) {
  const y = parseInt(watch?.year_mfg, 10);
  if (!y) return "[decade, e.g. 1960's]";
  return `${Math.floor(y / 10) * 10}'s`;
}

const WINDING = {
  Manual: "Manual Wind",
  "Manual Wind": "Manual Wind",
  Automatic: "Automatic",
  Auto: "Automatic",
  Quartz: "Quartz",
};

function commonBits(watch, { servicesCount = 0 } = {}) {
  const w = watch || {};
  const hasVideo = !!w.video_movement;
  const hasServices = Number(servicesCount) > 0;
  // Reference number: prefer the Excel ID, fall back to serial. Used as the
  // listing's "Reference" so buyers (and your records) can map back to the
  // workshop row.
  const reference = w.excel_id ? `TD-${w.excel_id}` : (w.serial || "[reference]");
  return {
    w,
    hasVideo,
    hasServices,
    decade: decadeOf(w),
    brand: w.brand || "[brand]",
    jewels: w.jewels || "[n]",
    jewelsLabel: w.jewels ? `${w.jewels} Jewel` : "[n] Jewel",
    caliber: w.caliber || "[caliber]",
    winding: WINDING[w.movement_type] || w.movement_type || "[Manual Wind / Automatic]",
    price: w.listing_price || w.sale_price || w.min_sale_price || null,
    reference,
    // Case & band — fall back to bracketed placeholders the user can replace.
    caseSize: w.case_size || "[mm]",
    caseMaterial: w.case_material || "[Base Metal / Stainless Steel / Gold-filled / Solid Gold]",
    caseBackType: w.case_back_type || "[Screwback / Snap / Display]",
    lugWidth: w.lug_width || "[mm]",
    bandWidth: w.band_width || "[mm]",
    bandMaterial: w.band_material || "[Leather / Bracelet / Rubber]",
  };
}

// ---- eBay (long-form, per the seller's sample) ---------------------------
function buildEbay(watch, opts) {
  const c = commonBits(watch, opts);
  const titleParts = [c.decade, "Men's", c.brand, c.jewelsLabel, c.winding, c.caliber, "Wristwatch"];
  if (c.hasServices) titleParts.push("Serviced");
  const title = titleParts.filter(Boolean).join(" ");

  const serviceSentence = c.hasServices
    ? "The watch has been totally disassembled and serviced. The watch runs and keeps time. "
    : "[Add your own running / condition summary here.] ";
  const overview =
    `This is a running ${c.decade} ${c.brand} ${c.winding.toLowerCase()} watch that has been ${c.hasServices ? "SERVICED" : "[serviced/inspected]"} and is fully functional. ` +
    `The watch has a ${c.caliber} movement. ${serviceSentence}` +
    `The watch has a very nice case, a clear crystal and a nice dial. [Optional: mention complications, band.]`;

  const body = [
    bold(title),
    `Reference: ${c.reference}`,
    "",
    bold("Overview"),
    overview,
    "",
    bold("Movement"),
    `• Caliber: ${c.caliber}`,
    `• Jewels: ${c.jewels}`,
    `• Type: ${c.winding}`,
    `• Service: ${c.hasServices ? "Inspected, cleaned, oiled and regulated" : "[service summary]"}`,
    `• Performance: ${c.hasVideo ? "See Video" : "[describe timegrapher / amplitude / rate]"}`,
    "• Notes: [e.g. winds and sets easily]",
    "",
    bold("Case"),
    `• Material: ${c.caseMaterial}`,
    `• Era: ${c.decade}`,
    "• Condition: [Cosmetic summary — crystal, case wear, dial condition]",
    `• Lug Width: ${c.lugWidth}`,
    `• Caseback: ${c.caseBackType}`,
    `• Case Size: ${c.caseSize}`,
    "• Crystal: [Acrylic / Mineral / Sapphire — clarity]",
    "",
    bold("Dial & Hands"),
    "• Dial: [Color / texture]",
    "• Indices: [Arabic / Roman / Batons]",
    "• Hands: [Style, material]",
    "",
    `${bold("Strap / Bracelet:")} ${c.bandMaterial} · ${c.bandWidth}`,
    "",
    "",
    bold("Condition Summary"),
    "• Fully functional",
    "• [Crystal condition]",
    "• Watch only",
    c.hasVideo ? "• Video of movement performance" : null,
    "",
    bold("About the Seller"),
    "I specialize in vintage mechanical watches with a focus on honest presentation, " +
    "accurate descriptions, and respect for the craft of horology. Every watch I list is " +
    "personally inspected, tested, and described with transparency and care. If a watch is " +
    "listed as serviced, it has been fully inspected, ultrasonically cleaned, properly oiled, " +
    "and carefully regulated.",
  ].filter((l) => l !== null).join("\n");

  return { title, body };
}

// ---- Chrono24 (tight, spec-first, buyers skim) ---------------------------
function buildChrono24(watch, opts) {
  const c = commonBits(watch, opts);
  const title = [c.brand, c.caliber, c.decade, c.hasServices ? "Serviced" : null]
    .filter(Boolean).join(" · ");
  const body = [
    bold(`${c.brand} ${c.caliber} — ${c.decade}`),
    `Reference: ${c.reference}`,
    "",
    bold("Reference / Movement"),
    `• Brand: ${c.brand}`,
    `• Caliber: ${c.caliber}`,
    `• Movement type: ${c.winding}`,
    `• Jewels: ${c.jewels}`,
    `• Year: ${c.w.year_mfg || "[year]"}`,
    c.w.serial ? `• Serial: ${c.w.serial}` : "• Serial: [on file, provided to serious buyers]",
    "",
    bold("Case & Dial"),
    `• Case material: ${c.caseMaterial}`,
    `• Case size: ${c.caseSize}`,
    `• Case back: ${c.caseBackType}`,
    `• Lug width: ${c.lugWidth}`,
    "• Crystal: [acrylic / mineral / sapphire]",
    "• Dial: [colour & condition]",
    "",
    bold("Strap / Bracelet"),
    `• Material: ${c.bandMaterial}`,
    `• Width: ${c.bandWidth}`,
    "",
    bold("Service History"),
    c.hasServices
      ? "Fully serviced in-house — disassembled, ultrasonically cleaned, oiled, regulated. Bench timing & amplitude figures on request."
      : "[Service summary — last service date, what was done]",
    c.hasVideo ? "Video of movement running available on request." : "",
    "",
    bold("Delivery"),
    "• Ships worldwide from [country]",
    "• Insured, tracked shipment",
    "• 14-day inspection period",
    "",
    "Questions welcome — happy to provide additional photos, timegrapher readouts, or video.",
  ].filter((l) => l !== null).join("\n");
  return { title, body };
}

// ---- Instagram (caption, hashtags, short) --------------------------------
function buildInstagram(watch, opts) {
  const c = commonBits(watch, opts);
  const title = `${c.brand} ${c.caliber} — ${c.decade}`;
  const hashtag = (s) => "#" + String(s).replace(/[^A-Za-z0-9]+/g, "").toLowerCase();
  const tags = [
    "vintagewatch", "watchcollector", "mechanicalwatch", "horology",
    "watchesofinstagram", "watchrestoration",
    hashtag(c.brand).replace(/^#$/, ""),
    c.caliber && c.caliber !== "[caliber]" ? hashtag(`caliber${c.caliber}`) : null,
    c.hasServices ? "freshlyserviced" : null,
  ].filter((t) => t && t.length > 1).map((t) => (t.startsWith("#") ? t : "#" + t));

  const body = [
    bold(`${c.decade} ${c.brand} — ${c.winding.toLowerCase()}, ${c.jewelsLabel}.`),
    "",
    c.hasServices
      ? "Just off the bench — disassembled, cleaned, oiled, regulated. Running strong."
      : "[Condition line — running / needs service / project]",
    "",
    c.hasVideo ? "🎥 Swipe for a clip of the movement running." : "[Describe what's shown in the carousel]",
    "",
    "DM if you'd like more photos or are interested in acquiring.",
    "",
    tags.join(" "),
  ].join("\n");

  return { title, body };
}

// ---- Private Sale (one-page description for email / PDF) -----------------
function buildPrivate(watch, opts) {
  const c = commonBits(watch, opts);
  const title = `${c.brand} ${c.caliber} — ${c.decade}`;
  const body = [
    bold(title),
    "",
    `${c.winding} movement · ${c.jewelsLabel} · caliber ${c.caliber}.`,
    c.hasServices
      ? "Recently serviced in-house — disassembled, ultrasonically cleaned, oiled, and regulated."
      : "[Summarise current running state & any recent work]",
    c.hasVideo ? "A video of the movement running is available on request." : "",
    "",
    "Case: [material, diameter, lug width, crystal].".replace(
      "[material, diameter, lug width, crystal]",
      `${c.caseMaterial}, ${c.caseSize}, ${c.lugWidth} lugs, [crystal]`,
    ),
    `Bracelet / Strap: ${c.bandMaterial}, ${c.bandWidth}.`,
    "",
    c.price ? `Asking: $${Number(c.price).toLocaleString()}` : "Asking: [price]",
    "",
    "Happy to answer questions or send additional photos. — [Your name]",
  ].filter(Boolean).join("\n");
  return { title, body };
}

// ---- Etsy (story-led + keyword-stuffed 140-char title + 13 tags) ---------
// Etsy SEO is heavily title-keyword driven and Etsy buyers respond to a
// craft/story narrative ("just off my bench…"), so we lead with that.
// Title: pack relevant search terms separated by `|` up to ~140 chars (Etsy's
//   actual limit is 140; we keep a safety margin).
// Tags: Etsy lets you add up to 13 tags, max 20 chars each — we surface a
//   "Suggested tags" line so the user can paste them straight in.
function buildEtsy(watch, opts) {
  const c = commonBits(watch, opts);

  // Build a comma-or-pipe separated title around 130-140 chars.
  const titleSeed = [
    `${c.decade} ${c.brand}`,
    c.caliber !== "[caliber]" ? `Cal ${c.caliber}` : null,
    `${c.jewelsLabel} ${c.winding}`,
    "Vintage Mens Watch",
    c.hasServices ? "Serviced" : null,
    "Mechanical Wristwatch",
    "Restored",
  ].filter(Boolean);
  let title = titleSeed.join(" | ");
  if (title.length > 140) title = title.slice(0, 137).replace(/\s+\S*$/, "") + "…";

  const tagSeed = [
    "vintage watch", "mens watch", "mechanical watch",
    c.brand !== "[brand]" ? c.brand.toLowerCase() : null,
    c.caliber !== "[caliber]" ? `caliber ${c.caliber}`.toLowerCase() : null,
    c.winding.toLowerCase(),
    `${c.decade.replace(/'s$/, "s")} watch`,
    c.hasServices ? "serviced watch" : null,
    "wristwatch", "horology", "watch collector",
    "swiss made", "vintage gift",
  ].filter(Boolean)
    .map((t) => t.replace(/\s+/g, " ").trim().slice(0, 20))
    .filter((t, i, a) => t && a.indexOf(t) === i)  // dedupe
    .slice(0, 13);

  const body = [
    bold(`${c.decade} ${c.brand} ${c.caliber !== "[caliber]" ? `Caliber ${c.caliber} ` : ""}— ${c.winding}, ${c.jewelsLabel}.`),
    "",
    c.hasServices
      ? "Just off my bench. Fully serviced — disassembled, ultrasonically cleaned, oiled with proper Moebius lubricants, regulated, and timed. Ready to wear and built to last another generation."
      : "[Story line — what drew you to this piece, where you sourced it, current running state.]",
    c.hasVideo ? "A short video of the movement running is available on request." : "",
    "",
    "✦ " + bold("MOVEMENT"),
    `• Caliber ${c.caliber}`,
    `• ${c.winding}, ${c.jewelsLabel}`,
    `• Year: ${c.w.year_mfg || "[year]"}`,
    "",
    "✦ " + bold("CASE & DIAL"),
    `• Case: ${c.caseMaterial}`,
    `• Diameter: ${c.caseSize}`,
    `• Case back: ${c.caseBackType}`,
    `• Lug width: ${c.lugWidth}`,
    "• Crystal: [acrylic / mineral / sapphire]",
    "• Dial: [colour & condition]",
    "",
    "✦ " + bold("STRAP"),
    `• ${c.bandMaterial}, ${c.bandWidth}`,
    "",
    "✦ " + bold("WHAT'S INCLUDED"),
    "• The watch as photographed",
    "• Insured, tracked shipping (signature on delivery)",
    "• 14-day inspection period — full refund minus return shipping if not as described",
    "",
    "Vintage watches are exactly that — vintage. Each piece carries the marks of a life lived. " +
    "I describe condition honestly and photograph from multiple angles. If anything is unclear, " +
    "please message me before purchase — I'm a real person on the other end and happy to help.",
    "",
    "─────",
    "Suggested Etsy tags (paste into the 13 tag slots):",
    tagSeed.join(", "),
  ].filter((l) => l !== null).join("\n");

  return { title, body };
}


export function buildListingTemplate(watch, opts = {}) {
  const ch = opts.channel || "eBay";
  switch (ch) {
    case "Etsy":
      return buildEtsy(watch, opts);
    case "Chrono24":
      return buildChrono24(watch, opts);
    case "Instagram":
      return buildInstagram(watch, opts);
    case "Private Sale":
      return buildPrivate(watch, opts);
    case "eBay":
    case "Facebook Marketplace":
    case "Local Dealer":
    case "Multi-channel":
    default:
      return buildEbay(watch, opts);
  }
}

// Back-compat alias — older imports used `buildEbayTemplate`.
export const buildEbayTemplate = (watch, opts = {}) =>
  buildListingTemplate(watch, { ...opts, channel: "eBay" });
